import React, { useEffect, useContext, useState, useRef, Suspense, useCallback  } from "react";
import * as survey from "@aidkitorg/types/lib/survey";
import InterfaceContext, { UserInfoContext } from "../../Context";
import { getDistroBrowserIncompatibility } from "../../Util";
import { useLocation } from "react-router-dom";
import { usePost } from "../../API";
import { Button, useToast } from "@aidkitorg/component-library";
import { useAsyncEffect } from "../../Hooks/AsyncEffect";

const Distro = React.lazy(() => import("@aidkitorg/typesheets/lib/distroeditor"));

export function MessageTemplateConfig() {
  const context = useContext(InterfaceContext);
  const user = useContext(UserInfoContext);

  const [messageTemplate, setMessageTemplate] = useState<survey.MessageTemplate>({
    kind: "Message Template",
    version: 1,
    name: "",
    description: "",
    instructions: []
  });
  const [exists, setExists] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const distroRef = useRef<React.ComponentRef<typeof Distro>>(null);

  const createTemplateRequest = usePost('/program/admin/prompts/create');
  const updateTemplateRequest = usePost('/program/admin/prompts/update');
  const findTemplateRequest = usePost('/program/admin/prompts/list');

  const { toast } = useToast();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search)

  const name = queryParams.get('name') ?? "";
  const version = queryParams.get('version') ?? "";

  useAsyncEffect(async () => {
    let templateToUse = messageTemplate;
    if (name) {
      const { items: templates } = await findTemplateRequest({ name, version: version ? parseInt(version) : undefined });
      const existingTemplate = templates?.[templates?.length - 1];
      if (existingTemplate) {
        setExists(true);
        templateToUse = { ...existingTemplate, kind: 'Message Template' };
        setMessageTemplate(templateToUse);
        setInitialized(false);
      }
    }
  }, [user, name, version]);

  let loadDistroTimer = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (initialized) return;
    // Initialize the distro editor with the message template
    // Wait for distroRef to be available
    if (loadDistroTimer.current) clearTimeout(loadDistroTimer.current);
    loadDistroTimer.current = setTimeout(() => {
      if (!distroRef.current) return;
      // Initializing with a random uid avoids an issue with the editor not updating when the user changes the value.
      distroRef.current.initialize(messageTemplate, { uid: user.uid + '-' + Math.random().toString(36).substring(2, 15) }, undefined, undefined, {},
        `We are using a tool to configure message templates for AI conversations. 
            Message templates define the structure and instructions for AI interactions.`
      );
      setInitialized(true);
    }, 250);
    return () => {
      if (loadDistroTimer.current) clearTimeout(loadDistroTimer.current);
    };
  }, [user, messageTemplate, initialized]);

  const saveTemplate = useCallback(async () => {
    if (exists) {
      const newVersion = (messageTemplate.version || 0) + 1;
      await createTemplateRequest({ item: { ...messageTemplate, version: newVersion } });
      setMessageTemplate(prev => ({ ...prev, version: newVersion }));
      toast({
        description: `created new template version: ${messageTemplate.version} => ${newVersion}`,
        autoClose: true,
        duration: 1_500
      });
    } else {
      await createTemplateRequest({ item: messageTemplate });
      toast({
        description: 'created new template!',
        autoClose: true,
        duration: 1_500
      });
    }
  }, [createTemplateRequest, messageTemplate]);

  const updateTemplate = useCallback(async () => {
    await updateTemplateRequest({ item: messageTemplate });
    toast({
      description: 'updated template!',
      autoClose: true,
      duration: 1_500
    });
  }, [updateTemplateRequest, messageTemplate]);

  const incompatible = getDistroBrowserIncompatibility();
  if (incompatible) return incompatible;

  return <div className="bg-gray-100 h-full p-4">
    <h1>Prompt Management</h1>
    {/** Editor for the current message template */}
    <div className="mt-4 bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg space-y-4 p-2">
      <h2 className="text-lg font-semibold">Editor</h2>
      <Suspense>
        <Distro
          ref={distroRef}
          types='src/survey.ts'
          name='MessageTemplate'
          onChange={(v) => {
            setMessageTemplate(v)
          }}
        />
      </Suspense>

      <div className='flex flex-col items-center gap-8'>
        <div className='flex flex-row gap-4'>
          <Button
            className="ml-2 mb-2"
            variant="primary"
            onClick={saveTemplate}>
            {exists ? 'Save New Version' : 'Save'}
          </Button>
          <Button
            className="ml-2 mb-2"
            variant="secondary"
            disabled={!exists}
            onClick={updateTemplate}>Update</Button>
        </div>
      </div>
    </div>
  </div>;
}
